import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../lib/api';

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const token = searchParams.get('token');

  useEffect(() => {
    if (!token) {
      setError('Invalid reset link. Token is missing.');
    }
  }, [token]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    setLoading(true);

    try {
      await api.post('/auth/reset-password', { 
        token, 
        newPassword: password 
      });
      setSuccess(true);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  const getPasswordStrength = () => {
    if (password.length === 0) return { label: '', color: '' };
    if (password.length < 8) return { label: 'Too short', color: '#ef4444' };
    if (password.length < 12) return { label: 'Weak', color: '#f59e0b' };
    if (password.length < 16) return { label: 'Good', color: '#10b981' };
    return { label: 'Strong', color: '#059669' };
  };

  const strength = getPasswordStrength();

  if (success) {
    return (
      <div style={{ 
        minHeight: '100vh', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #13afe3 0%, #6366f1 100%)'
      }}>
        <div style={{
          background: 'white',
          padding: '48px',
          borderRadius: '12px',
          boxShadow: '0 10px 40px rgba(0,0,0,0.1)',
          maxWidth: '500px',
          width: '90%',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '64px', marginBottom: '24px' }}>✅</div>
          <h2 style={{ marginBottom: '16px', color: '#1a1a1a' }}>Password Reset Successful!</h2>
          <p style={{ color: '#666', marginBottom: '32px', lineHeight: '1.6' }}>
            Your password has been reset successfully. You can now login with your new password.
          </p>
          <button
            onClick={() => navigate('/login')}
            style={{
              width: '100%',
              padding: '14px',
              background: '#13afe3',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              fontSize: '16px',
              fontWeight: 'bold',
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
            onMouseOver={(e) => e.currentTarget.style.background = '#0f8ec4'}
            onMouseOut={(e) => e.currentTarget.style.background = '#13afe3'}
          >
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  if (!token) {
    return (
      <div style={{ 
        minHeight: '100vh', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #13afe3 0%, #6366f1 100%)'
      }}>
        <div style={{
          background: 'white',
          padding: '48px',
          borderRadius: '12px',
          boxShadow: '0 10px 40px rgba(0,0,0,0.1)',
          maxWidth: '500px',
          width: '90%',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '64px', marginBottom: '24px' }}>❌</div>
          <h2 style={{ marginBottom: '16px', color: '#1a1a1a' }}>Invalid Reset Link</h2>
          <p style={{ color: '#666', marginBottom: '32px', lineHeight: '1.6' }}>
            This password reset link is invalid or has expired. Please request a new one.
          </p>
          <button
            onClick={() => navigate('/forgot-password')}
            style={{
              width: '100%',
              padding: '14px',
              background: '#13afe3',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              fontSize: '16px',
              fontWeight: 'bold',
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
            onMouseOver={(e) => e.currentTarget.style.background = '#0f8ec4'}
            onMouseOut={(e) => e.currentTarget.style.background = '#13afe3'}
          >
            Request New Link
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ 
      minHeight: '100vh', 
      display: 'flex', 
      alignItems: 'center', 
      justifyContent: 'center',
      background: 'linear-gradient(135deg, #13afe3 0%, #6366f1 100%)'
    }}>
      <div style={{
        background: 'white',
        padding: '48px',
        borderRadius: '12px',
        boxShadow: '0 10px 40px rgba(0,0,0,0.1)',
        maxWidth: '450px',
        width: '90%'
      }}>
        <div style={{ textAlign: 'center', marginBottom: '32px' }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>🔑</div>
          <h1 style={{ margin: 0, fontSize: '28px', color: '#1a1a1a' }}>Reset Your Password</h1>
          <p style={{ color: '#666', marginTop: '8px', fontSize: '14px' }}>
            Enter your new password below
          </p>
        </div>

        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '8px', color: '#444', fontWeight: '500' }}>
              New Password
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              placeholder="Enter new password"
              style={{
                width: '100%',
                padding: '12px',
                border: '2px solid #e5e7eb',
                borderRadius: '6px',
                fontSize: '16px',
                boxSizing: 'border-box',
                transition: 'border-color 0.2s'
              }}
              onFocus={(e) => e.currentTarget.style.borderColor = '#13afe3'}
              onBlur={(e) => e.currentTarget.style.borderColor = '#e5e7eb'}
            />
            {password && (
              <div style={{ marginTop: '8px', fontSize: '12px' }}>
                Password strength: <span style={{ color: strength.color, fontWeight: 'bold' }}>{strength.label}</span>
              </div>
            )}
          </div>

          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '8px', color: '#444', fontWeight: '500' }}>
              Confirm Password
            </label>
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              placeholder="Confirm new password"
              style={{
                width: '100%',
                padding: '12px',
                border: '2px solid #e5e7eb',
                borderRadius: '6px',
                fontSize: '16px',
                boxSizing: 'border-box',
                transition: 'border-color 0.2s'
              }}
              onFocus={(e) => e.currentTarget.style.borderColor = '#13afe3'}
              onBlur={(e) => e.currentTarget.style.borderColor = '#e5e7eb'}
            />
            {confirmPassword && password !== confirmPassword && (
              <div style={{ marginTop: '8px', fontSize: '12px', color: '#ef4444' }}>
                Passwords do not match
              </div>
            )}
          </div>

          {error && (
            <div style={{
              background: '#fee2e2',
              border: '1px solid #fca5a5',
              color: '#991b1b',
              padding: '12px',
              borderRadius: '6px',
              marginBottom: '20px',
              fontSize: '14px'
            }}>
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={loading || password !== confirmPassword || password.length < 8}
            style={{
              width: '100%',
              padding: '14px',
              background: (loading || password !== confirmPassword || password.length < 8) ? '#9ca3af' : '#13afe3',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              fontSize: '16px',
              fontWeight: 'bold',
              cursor: (loading || password !== confirmPassword || password.length < 8) ? 'not-allowed' : 'pointer',
              transition: 'all 0.2s'
            }}
            onMouseOver={(e) => {
              if (!loading && password === confirmPassword && password.length >= 8) {
                e.currentTarget.style.background = '#0f8ec4';
              }
            }}
            onMouseOut={(e) => {
              if (!loading && password === confirmPassword && password.length >= 8) {
                e.currentTarget.style.background = '#13afe3';
              }
            }}
          >
            {loading ? 'Resetting...' : 'Reset Password'}
          </button>

          <div style={{ textAlign: 'center', marginTop: '20px' }}>
            <a
              onClick={() => navigate('/login')}
              style={{
                color: '#13afe3',
                textDecoration: 'none',
                cursor: 'pointer',
                fontSize: '14px'
              }}
            >
              ← Back to Login
            </a>
          </div>
        </form>
      </div>
    </div>
  );
}
